import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppName } from 'sl-api-connector';
import UnstyledButton from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/UnstyledFilterButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { useCompanyName, useMainEntityId, useMainEntityName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { useCategoryEntity } from 'src/utils/Hooks/reduxSelectors';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { getAppName } from 'src/utils/app';
/**
 * Hook to get the category and subcategory filter count, and the active categories and subcategories
 * @returns Object containing the category and subcategory filter count, and the active categories and subcategories
 */
const useTaxonomyFilterMetaData = () => {
  const mainEntityId = useMainEntityId();
  const categories = useAppSelector((state) => state?.filters?.category);
  const subcategories = useAppSelector((state) => state?.filters?.subcategory);
  const segment = useAppSelector((state) => state?.filters?.segment);

  const businessUnitsIds = useAppSelector((state) => state?.filters?.businessUnits);
  const savedSearchesById = useAppSelector((state) => state?.segments?.savedSearchesById);
  const businessUnits = businessUnitsIds?.filter((el) => el !== mainEntityId)?.map((id) => savedSearchesById.get(id));

  const categoryFilterCount = categories?.length || 0;
  const subcategoryFilterCount = subcategories?.length || 0;
  const businessUnitFilterCount = businessUnits?.length || 0;

  return {
    categoryFilterCount,
    subcategoryFilterCount,
    businessUnitFilterCount,
    activeCategories: categories || [],
    activeSubcategories: subcategories || [],
    activeSegment: segment,
    activeBusinessUnits: businessUnits
  };
};
interface TextLinkProps {
  text: string;
  onClick?: () => void;
  link?: string;
}

/**
 * Component to display text with an optional link or onClick function.
 * Used to create clickable text as a button or link for the dynamic subtitle
 */
const ActionText = ({ text, onClick, link }: TextLinkProps) => {
  if (link) {
    return (
      <Text sx={{ display: 'inline' }} underlined variant="body1">
        <Link to={link} target="_blank" rel="noopener noreferrer">
          <span style={{ textDecoration: 'underline' }}>{text}</span>
        </Link>
      </Text>
    );
  } else {
    return (
      <UnstyledButton style={{ display: 'inline' }}>
        <Text sx={{ display: 'inline' }} underlined variant="body1" onClick={onClick}>
          {text}
        </Text>
      </UnstyledButton>
    );
  }
};

/**
 *
 * @returns Function that returns a link to a taxonomy page (category/subcategory) based on the id and taxonomy type
 */
const useGetTaxonomyLink = () => {
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);

  return useCallback(
    (id: string | number, taxonomyType: 'category' | 'subcategory' | 'businessunit' | 'segment') => {
      return `/${taxonomyType}/${id}${searchParams}${additionalParams}`;
    },
    [searchParams, additionalParams]
  );
};

// Mapping for taxonomy types
const taxonomyLabels = {
  category: { singular: 'category', plural: 'categories' },
  subcategory: { singular: 'subcategory', plural: 'subcategories' },
  businessunit: { singular: 'business unit', plural: 'business units' }
};

/**
 *
 * @returns Object containing the category and subcategory text, and the break text
 */
export const useTaxonomySubtitleText = () => {
  const { hasActiveFilters, numberOfActiveFilters } = useBeaconFilters();
  const entityType = useMainEntityType();
  const getTaxonomyLink = useGetTaxonomyLink();
  const categories = useAppSelector((state) => state.categories);
  const {
    categoryFilterCount,
    activeCategories,
    subcategoryFilterCount,
    activeSubcategories,
    businessUnitFilterCount,
    activeBusinessUnits,
    activeSegment
  } = useTaxonomyFilterMetaData();

  const [showOtherMap, setShowOtherMap] = useState({ category: false, subcategory: false, businessUnit: false });

  const handleOtherClick = (taxonomyType: string) => {
    setShowOtherMap((prev) => ({
      ...prev,
      [taxonomyType]: !prev[taxonomyType]
    }));
  };

  // Temporary text for Atlas until we do revamp new subtitle logic - written -  Nov 19, 2025
  const tempAtlasText = useMemo(() => {
    if (numberOfActiveFilters === 0 || !['company', 'brand', 'segment'].includes(entityType)) {
      return [];
    }

    return numberOfActiveFilters > 1 ? `in ${numberOfActiveFilters} selections` : 'in 1 selection';
  }, [numberOfActiveFilters, entityType]);

  const formatText = useCallback(
    (taxonomyType: 'category' | 'subcategory' | 'businessunit', activeTaxonomies: any[], filterCount: number) => {
      if (filterCount === 0) {
        return [];
      }

      const { singular, plural } = taxonomyLabels[taxonomyType];

      const taxonomyText = activeTaxonomies.map((taxonomy) => {
        return (
          <ActionText link={getTaxonomyLink(taxonomy.id, taxonomyType)} key={taxonomy.id} text={taxonomy.displayName} />
        );
      });

      if (filterCount === 1) {
        return [' from the ', taxonomyText[0], ` ${singular}`];
      }

      if (filterCount === 2) {
        return [' from the ', taxonomyText[0], ' and ', taxonomyText[1], ` ${plural}`];
      }

      if (showOtherMap[taxonomyType]) {
        const taxonomyList: (string | React.JSX.Element)[] = taxonomyText.reduce((acc, curr, index) => {
          if (index === taxonomyText.length - 1) {
            acc.push(' and ', curr);
          } else {
            acc.push(curr, ', ');
          }

          return acc;
        }, []);

        return [' from the ', ...taxonomyList, ` ${plural}`];
      }

      return [
        ' from the ',
        taxonomyText[0],
        ', ',
        taxonomyText[1],
        ', and ',
        <ActionText onClick={() => handleOtherClick(taxonomyType)} key={`${taxonomyType}Other`} text="other" />,
        ` ${plural}`
      ];
    },
    [showOtherMap, getTaxonomyLink]
  );

  const segmentText = useMemo(() => {
    if (!activeSegment) {
      return [];
    }

    return [
      ' from the ',
      <ActionText link={getTaxonomyLink(activeSegment.id, 'segment')} key={activeSegment.id} text="applied segment" />
    ];
  }, [activeSegment, getTaxonomyLink]);

  const categoryText = useMemo(() => {
    if (hasActiveFilters) {
      return formatText('category', activeCategories, categoryFilterCount);
    } else if (entityType === 'client') {
      return [' from all ', `${categories?.length - 1 || 0}`, ' account categories'];
    } else {
      return [];
    }
  }, [categories, hasActiveFilters, categoryFilterCount, activeCategories, entityType, formatText]);

  const subcategoryText = useMemo(() => {
    if (hasActiveFilters) {
      return formatText('subcategory', activeSubcategories, subcategoryFilterCount);
    } else {
      return [];
    }
  }, [hasActiveFilters, subcategoryFilterCount, activeSubcategories, formatText]);

  const businessUnitText = useMemo(() => {
    if (hasActiveFilters) {
      return formatText('businessunit', activeBusinessUnits, businessUnitFilterCount);
    } else {
      return [];
    }
  }, [hasActiveFilters, businessUnitFilterCount, activeBusinessUnits, formatText]);

  if (numberOfActiveFilters > 0 && getAppName() === AppName.Atlas) {
    return tempAtlasText;
  }

  // Filters taxonomy order array
  const taxonomies = [businessUnitText, categoryText, subcategoryText].filter(Boolean);

  if (segmentText) {
    return segmentText;
  } else if (taxonomies.length === 3) {
    return [...taxonomies[0], ', ', ...taxonomies[1], ' and ', ...taxonomies[2]];
  } else if (taxonomies.length === 2) {
    return [...taxonomies[0], ' and ', ...taxonomies[1]];
  } else if (taxonomies.length === 1) {
    return taxonomies[0];
  }

  return [];
};

interface UseEntityPhrasingParams {
  /**
   * Returns only the title of the entity name. Default is false.
   * @example "{category name}" instead of "the {category name} category"
   */
  titleOnly?: boolean;
}
/**
 * Hook to get the entity phrasing based on the entity type
 */
export const useEntityPhrasing = ({ titleOnly = false }: UseEntityPhrasingParams = {}): string => {
  const entityType = useMainEntityType();
  const companyName = useCompanyName();
  const entityName = useMainEntityName();
  const { isAllCategoriesEntity } = useCategoryEntity();
  const categories = useAppSelector((state) => state.categories);
  const { numberOfActiveFilters } = useBeaconFilters();

  const entityTitle = {
    client: entityName,
    company: companyName,
    adEntity: entityName,
    brand: entityName,
    adPortfolio: entityName,
    segment: entityName,
    searchtermlist: companyName,
    topic: entityName,
    topicgroup: entityName,
    category: entityName,
    subcategory: entityName,
    product: entityName,
    adCampaign: entityName,
    businessunit: entityName
  }[entityType];

  if (titleOnly) {
    return entityTitle;
  }

  if (
    numberOfActiveFilters > 0 &&
    getAppName() === AppName.Atlas &&
    !['brand', 'client', 'company', 'segment'].includes(entityType)
  ) {
    return numberOfActiveFilters === 1 ? '1 selection' : `${numberOfActiveFilters} selections`;
  }

  switch (entityType) {
    case 'product':
      return `this product`;

    case 'brand':
    case 'client':
    case 'company':
      return entityTitle;

    case 'businessunit':
      return `the business unit`;

    case 'adCampaign':
      return `the ${entityName} campaign`;

    case 'topic':
      return `the ${entityTitle} topic`;

    case 'topicgroup':
      return `the ${entityTitle} topic group`;

    case 'adEntity':
      return `the ${entityName} entity`;

    case 'adPortfolio':
      return `the ${entityName} portfolio`;

    case 'category':
      return isAllCategoriesEntity ? `all ${categories?.length - 1} account categories` : `the ${entityTitle} category`;

    default:
      return `the ${entityTitle || entityName} ${entityType}`;
  }
};

/**
 * Optional helper function to remove any undefined or empty parts of the subtitle. Helps remove empty space before punctuation when parts are just empty strings.
 * NOTE: this the order of the parts will be preserved.
 * @param parts Collection containing the subtitles parts which can be strings or arrays of strings and JSX elements.
 * @returns Collection of subtitle parts with any undefined or empty parts removed.
 */
export const refineSubtitle = (parts: (string | (string | React.JSX.Element)[])[]) => {
  return parts.filter((part) => part !== undefined && part !== '');
};
